import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { apiFetch } from '../App';
import { ga } from '../utils/ga4';
import { applySEO } from '../utils/seo';
import { sharePage } from '../utils/share';
import { Calendar, ArrowLeft, Tag, Loader2, Share2, Clock } from 'lucide-react';

const PAW = 'https://customer-assets.emergentagent.com/job_paw-portal-3/artifacts/r89kksa2_Petfolk%20Paw%20Multi.png';

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
}

function readingTime(html) {
  if (!html) return 1;
  const text = html.replace(/<[^>]+>/g, ' ').trim();
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [error, setError] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setBlog(null); setError(null);
    apiFetch(`/api/blog/${slug}`)
      .then(b => {
        setBlog(b);
        ga.blogView(b.slug, b.category, b.title);
      })
      .catch(() => setError('Post not found'));
  }, [slug]);

  // Inject SEO meta + JSON-LD Article schema (Helmet v2 unreliable for meta updates)
  useEffect(() => {
    if (!blog) return;
    const canonical = `https://petfolk.in/blog/${blog.slug}`;
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: blog.title,
      description: blog.excerpt || '',
      image: blog.coverImageUrl || undefined,
      datePublished: blog.publishedAt || blog.createdAt,
      dateModified: blog.updatedAt,
      author: { '@type': 'Organization', name: blog.authorName || 'Petfolk' },
      publisher: {
        '@type': 'Organization',
        name: 'Petfolk',
        logo: { '@type': 'ImageObject', url: 'https://customer-assets.emergentagent.com/job_paw-portal-3/artifacts/ykqrxquz_Petfolk%20Logo%20Color%20TransparentBG.png' },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
    };
    return applySEO({
      title: `${blog.title} | Petfolk Blog`,
      description: blog.excerpt || blog.title,
      canonical,
      ogType: 'article',
      ogImage: blog.coverImageUrl,
      author: blog.authorName || 'Petfolk',
      articlePublishedTime: blog.publishedAt,
      articleModifiedTime: blog.updatedAt,
      articleSection: blog.category,
      articleTags: blog.tags || [],
      jsonLd: ld,
      jsonLdId: 'blog-jsonld',
    });
  }, [blog]);

  // Load related posts (same category)
  useEffect(() => {
    if (!blog?.category) return;
    apiFetch(`/api/blog?category=${encodeURIComponent(blog.category)}&limit=4`)
      .then(d => setRelated((d.items || []).filter(b => b.slug !== blog.slug).slice(0, 3)))
      .catch(() => {});
  }, [blog]);

  const handleShare = async () => {
    const method = await sharePage({
      title: blog?.title,
      text: blog?.excerpt,
      type: 'blog',
      slug: blog?.slug,
    });
    if (method === 'clipboard') alert('Link copied!');
    if (method && method !== 'cancelled') ga.blogShare(blog?.slug, method);
  };

  if (error) {
    return (
      <div className="min-h-screen pt-24 px-4 flex items-center justify-center" data-testid="blog-not-found">
        <div className="text-center">
          <h1 className="font-outfit text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Post not found</h1>
          <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>The article you're looking for may have been moved or unpublished.</p>
          <Link to="/blog" className="btn-primary inline-flex items-center gap-2"><ArrowLeft size={14} /> Back to blog</Link>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center" data-testid="blog-loading-detail">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  const sanitized = DOMPurify.sanitize(blog.content || '', { ADD_ATTR: ['target', 'rel'] });
  const canonical = `https://petfolk.in/blog/${blog.slug}`;
  const minutes = readingTime(blog.content);

  return (
    <div className="relative min-h-screen pt-16" style={{ background: 'var(--bg-primary)' }} data-testid="blog-post-page">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {/* Breadcrumb */}
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-medium mb-6 hover:text-petfolk-orange transition-colors" style={{ color: 'var(--text-secondary)' }} data-testid="blog-back-link">
          <ArrowLeft size={14} /> All articles
        </Link>

        {/* Header */}
        <header className="mb-8">
          {blog.category && (
            <Link to={`/blog?category=${encodeURIComponent(blog.category)}`} className="inline-flex items-center gap-1 mb-4 text-xs font-semibold uppercase tracking-wider text-petfolk-orange hover:underline">
              <Tag size={12} /> {blog.category}
            </Link>
          )}
          <h1 className="font-outfit font-bold leading-[1.1] tracking-tight text-3xl sm:text-4xl lg:text-5xl mb-5" style={{ color: 'var(--text-primary)' }}>
            {blog.title}
          </h1>
          {blog.excerpt && (
            <p className="text-base sm:text-lg leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{blog.excerpt}</p>
          )}
          <div className="flex flex-wrap items-center gap-4 mt-6 pb-6 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-petfolk-orange to-petfolk-purple flex items-center justify-center text-white text-xs font-bold">
                {(blog.authorName || 'P')[0]}
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{blog.authorName || 'Petfolk'}</p>
                <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                  <span className="inline-flex items-center gap-1"><Calendar size={11} /> {formatDate(blog.publishedAt)}</span>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1"><Clock size={11} /> {minutes} min read</span>
                </div>
              </div>
            </div>
            <button onClick={handleShare} className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium hover:bg-petfolk-orange/10 hover:border-petfolk-orange hover:text-petfolk-orange transition-colors" style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }} data-testid="blog-share-btn">
              <Share2 size={12} /> Share
            </button>
          </div>
        </header>

        {/* Cover image */}
        {blog.coverImageUrl && (
          <img src={blog.coverImageUrl} alt={blog.title} className="w-full rounded-2xl mb-8 aspect-[16/9] object-cover" />
        )}

        {/* Body */}
        <div
          className="petfolk-article prose prose-lg dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: sanitized }}
          data-testid="blog-content"
        />

        {/* Tags */}
        {blog.tags && blog.tags.length > 0 && (
          <div className="mt-10 pt-6 border-t flex flex-wrap items-center gap-2" style={{ borderColor: 'var(--border-color)' }}>
            <Tag size={14} style={{ color: 'var(--text-secondary)' }} />
            {blog.tags.map(t => (
              <span key={t} className="px-3 py-1 rounded-full text-xs font-medium" style={{ background: 'var(--bg-card)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}>{t}</span>
            ))}
          </div>
        )}

        {/* CTA — book grooming */}
        <div className="mt-12 rounded-2xl p-6 sm:p-8 bg-gradient-to-br from-petfolk-orange/10 via-petfolk-purple/5 to-petfolk-teal/10 border" style={{ borderColor: 'var(--border-color)' }}>
          <div className="flex items-start gap-4">
            <img src={PAW} alt="" className="w-12 h-12 shrink-0" />
            <div>
              <h3 className="font-outfit text-xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Ready to give your pet the care they deserve?</h3>
              <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>Book a Petfolk grooming session at any of our stores or mobile vans across India.</p>
              <Link to="/" onClick={() => ga.blogCtaClick(blog.slug, 'find_petfolk')} className="btn-primary inline-flex items-center gap-2" data-testid="blog-cta-book">
                Find a Petfolk near you <ArrowLeft size={14} className="rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Related posts */}
      {related.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16" data-testid="blog-related">
          <h2 className="font-outfit text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>More from {blog.category}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {related.map(b => (
              <Link key={b.id} to={`/blog/${b.slug}`} className="group rounded-2xl overflow-hidden border hover:shadow-lg hover:-translate-y-0.5 transition-all" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}>
                <div className="aspect-[16/10] overflow-hidden bg-gradient-to-br from-petfolk-orange/15 via-petfolk-purple/15 to-petfolk-teal/15 flex items-center justify-center">
                  {b.coverImageUrl ? (
                    <img src={b.coverImageUrl} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <img src={PAW} alt="" className="w-14 h-14 opacity-40" />
                  )}
                </div>
                <div className="p-4">
                  <h4 className="font-outfit text-base font-bold leading-snug line-clamp-2 group-hover:text-petfolk-orange transition-colors" style={{ color: 'var(--text-primary)' }}>{b.title}</h4>
                  <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>{formatDate(b.publishedAt)}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Article styles */}
      <style>{`
        .petfolk-article h2 { font-family: 'Outfit', sans-serif; font-size: 1.5rem; font-weight: 700; margin-top: 2rem; margin-bottom: 0.75rem; color: var(--text-primary); }
        .petfolk-article h3 { font-family: 'Outfit', sans-serif; font-size: 1.2rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 0.5rem; color: var(--text-primary); }
        .petfolk-article p { margin: 1rem 0; line-height: 1.75; color: var(--text-primary); font-size: 1.05rem; }
        .petfolk-article ul, .petfolk-article ol { margin: 1rem 0; padding-left: 1.5rem; }
        .petfolk-article ul { list-style: disc; }
        .petfolk-article ol { list-style: decimal; }
        .petfolk-article li { margin: 0.4rem 0; line-height: 1.7; color: var(--text-primary); }
        .petfolk-article blockquote { border-left: 4px solid #FF8C42; padding: 0.6rem 1rem; margin: 1.25rem 0; font-style: italic; color: var(--text-secondary); background: rgba(255, 140, 66, 0.05); border-radius: 4px; }
        .petfolk-article a { color: #FF8C42; text-decoration: underline; }
        .petfolk-article strong { color: var(--text-primary); font-weight: 700; }
        .petfolk-article img { max-width: 100%; height: auto; border-radius: 12px; margin: 1.25rem 0; }
      `}</style>
    </div>
  );
}
